import _ from 'lodash';

export const filterParams = [
  'statuses', 'types', 'orientation', 'visibility', 'extensions', 'user_ids', 'deleted_by', 'people', 'no_collections',
  'collections',
  'date', 'date_from', 'date_to',
  'original_date', 'original_date_from', 'original_date_to',
  'expiration_date', 'expiration_date_from', 'expiration_date_to',
  'terms',
];

export const searchFilterParams = [
  'search',
  'search_term',
  'search_fields',
  'query_type',
];

export const collectionFilterParams = [
  'visibility', 'user_ids', 'date', 'date_from', 'date_to',
];

export const arrayIndexes = [
  'search_fields',
  'selectedFileStatuses',
  'selectedUserActivationStatuses',
  'selectedRoles',
  'statuses',
  'types',
  'extensions',
  'user_ids',
  'deleted_by',
  'people',
  'collections',
  'visibility',
  'terms',
];

export const booleanIndexes = [
  'no_collections',
];

const dateIndexes = [
  'date_from', 'date_to',
  'original_date_from', 'original_date_to',
  'expiration_date_from', 'expiration_date_to',
];

export const getCurrentURLParams = (location) => {
  const queryParamsString = new URLSearchParams(location.search).toString();
  if (queryParamsString.length > 0) {
    return `?${queryParamsString.replace(/%2C/g, ',')}`;
  }
  return '';
};

const getPathnameWithSuffix = (history, pathSuffix) => {
  if (!pathSuffix) return {};

  const suffix = `/${pathSuffix.replace(/^\/+|\/+$/g, '')}`;
  const currentPath = history.location.pathname.replace(/\/+$/, '');
  const pageCollection = currentPath.match(/^(\/(?:pages|content)\/[^/]+)\/collection\/([^/]+)$/);

  if (suffix === '/search' && pageCollection) {
    return { pathname: `${pageCollection[1]}/search/collections/${pageCollection[2]}` };
  }

  if (currentPath.endsWith(suffix)) return {};

  return { pathname: `${currentPath}${suffix}` };
};

export const handleURLParams = (
  changedParams,
  history,
  locationState = {},
  pathSuffix = null,
) => {
  const { location } = window;
  const queryParams = new URLSearchParams(location.search);

  Object.keys(changedParams).forEach(key => {
    let value = changedParams[key];
    if (!value || (Array.isArray(value) && value.length === 0)) {
      queryParams.delete(key);
    } else {
      if (arrayIndexes.includes(key)) {
        value = value.join(',');
      }
      if (dateIndexes.includes(key)) {
        value = value.substring(0, 10);
      }
      queryParams.set(key, encodeURIComponent(value));
    }
  });

  history.replace({
    ...getPathnameWithSuffix(history, pathSuffix),
    search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
    state: locationState || {},
  });
};

const getDefaultParam = (key, defaultParams, emptyValue = '') => {
  if (_.has(defaultParams, key)) return defaultParams[key];
  if (arrayIndexes.includes(key)) return [];
  if (booleanIndexes.includes(key)) return null;
  if (['perPage'].includes(key)) return 20;
  if (['orderBy'].includes(key)) return 'created_at';
  if (['order'].includes(key)) return 'desc';
  if (['search'].includes(key)) return null;
  return emptyValue;
};

const paramsReducer = (key, queryParams, defaultParams) => {
  if (['page'].includes(key)) return Number(queryParams.get(key)) > 0 ? Number(queryParams.get(key)) : 1;
  if (['perPage'].includes(key)) return Number(queryParams.get(key)) > 0 ? Number(queryParams.get(key)) : getDefaultParam(key, defaultParams);
  if (['search', 'orderBy', 'order'].includes(key)) return queryParams.get(key) ? queryParams.get(key) : getDefaultParam(key, defaultParams);
  if (arrayIndexes.includes(key)) {
    return queryParams.get(key) ? queryParams.get(key).split(',') : getDefaultParam(key, defaultParams);
  }
  if (booleanIndexes.includes(key)) {
    return queryParams.get(key) ?
      queryParams.get(key) === 'true' : getDefaultParam(key, defaultParams);
  }

  return queryParams.get(key) ? queryParams.get(key) : getDefaultParam(key, defaultParams, null);
};

export const handleComponentParams = (
  force,
  paramNames,
  prevProps,
  updateState,
  updateStateCallback,
  defaultParams = {},
) => {
  const { location } = window;
  const queryParams = new URLSearchParams(location.search);

  if (force) {
    const params = paramNames.reduce(
      (o, key) => ({ ...o, [key]: paramsReducer(key, queryParams, defaultParams) }),
      {},
    );

    if (!_.isEmpty(params)) {
      if (_.isArray(updateState)) {
        paramNames.map((p, index) => {
          if (_.has(params, p)) {
            updateState[index](params[`${p}`]);
          }
          return p;
        });
      } else {
        updateState(() => (params), () => {
          updateStateCallback(params);
        });
      }
    }
  } else if (prevProps) {
    const prevQueryParams = new URLSearchParams(prevProps.location.search);

    const currentParams = paramNames.map(key => paramsReducer(key, queryParams, defaultParams));
    const prevParams = paramNames.map(key => paramsReducer(key, prevQueryParams, defaultParams));

    const params = {};
    currentParams.map((element, index) => {
      if (!_.isEqual(element, prevParams[index])) {
        params[paramNames[index]] = element;
      }
      return element;
    });

    if (!_.isEmpty(params)) {
      if (_.isArray(updateState)) {
        paramNames.map((p, index) => {
          if (_.has(params, p)) {
            updateState[index](params[`${p}`]);
          }
          return p;
        });
      } else {
        updateState(() => (params), () => {
          updateStateCallback(params);
        });
      }
    }
  }
};

export const handleURLParamsRemove = (key, value, history, locationState = {}) => {
  const { location } = window;
  const queryParams = new URLSearchParams(location.search);

  if (key === 'dates') {
    queryParams.delete('date');
    queryParams.delete('date_from');
    queryParams.delete('date_to');
    history.replace({
      search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
      state: locationState || {},
    });
  } else if (key === 'original_dates') {
    queryParams.delete('original_date');
    queryParams.delete('original_date_from');
    queryParams.delete('original_date_to');
    history.replace({
      search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
      state: locationState || {},
    });
  } else if (key === 'expiration_dates') {
    queryParams.delete('expiration_date');
    queryParams.delete('expiration_date_from');
    queryParams.delete('expiration_date_to');
    history.replace({
      search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
      state: locationState || {},
    });
  } else if (queryParams.get(key)) {
    if (arrayIndexes.includes(key)) {
      const params = queryParams.get(key).split(',').filter(p => p !== `${value}`);

      if (params.length === 0) {
        queryParams.delete(key);
        history.replace({
          search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
          state: locationState || {},
        });
      } else {
        queryParams.set(key, params.join(','));
        history.replace({
          search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
          state: locationState || {},
        });
      }
    } else {
      queryParams.delete(key);
      history.replace({
        search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
        state: locationState || {},
      });
    }
  }
};

export const handleURLParamsClear = (keys, history, locationState = {}) => {
  const { location } = window;
  const queryParams = new URLSearchParams(location.search);
  keys.map(key => queryParams.delete(key));

  history.replace({
    search: decodeURIComponent(queryParams.toString().replace(/%2C/g, ',')),
    state: locationState || {},
  });
};

export const prepareSearchFilters = (filters) => {
  const newFilters = {};
  filters.forEach(f => {
    if (arrayIndexes.includes(f.type)) {
      if (newFilters[f.type]) {
        newFilters[f.type].push(f.key);
      } else {
        newFilters[f.type] = [f.key];
      }
    } else {
      newFilters[f.type] = f.key;
    }
  });

  return newFilters;
};
